import { dateLib, moneyLib, roomLib, tenantLib } from "@agentcall/core/lib"
import { formatPhone } from "@agentcall/core/lib/tenant_lib"
import { tenantType } from "@agentcall/core/type"
import { Plus } from "lucide-react"
import { Suspense, useEffect, useState } from "react"
import {
  ArrowLeft,
  Book,
  CreditCard,
  Edit,
  FileText,
  MoreHorizontal,
  Repeat,
  User,
  Users,
} from "react-feather"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import { roomApi, roomParams } from "@/api/client/room"
import { TenantUtils } from "@/api/client/tenant"
import type { RoomGet200RoomTenantsItem } from "@/api/generated/models"
import { EchoesSyncCard } from "@/components/echoes/EchoesSyncCard"
import { Button } from "@/components/ui/button"
import { EmptyCard } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu/DropdownMenu"
import { ErrorPage } from "@/components/ui/error"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { ImagePreviewDialog, type PreviewImage } from "@/components/ui/image-preview-dialog"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner/Spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { usePermission } from "@/features/auth/hooks/usePermission"
import { openInNewTab } from "@/lib/navigation"

interface FeeDetail {
  label: string
  amount: number
  baseAmount: number
  tax: number
}

// 料金明細を生成するヘルパー関数
function generateFeeDetails(tenant: RoomGet200RoomTenantsItem): FeeDetail[] {
  const fees: FeeDetail[] = []
  const isResidential = tenant.purpose === tenantType.TENANT_PURPOSE_LIVING

  // 家賃
  const rentTax = isResidential ? 0 : Math.floor(tenant.rent * moneyLib.TAX_RATE)
  fees.push({
    label: "家賃",
    baseAmount: tenant.rent,
    tax: rentTax,
    amount: tenant.rent + rentTax,
  })

  // 共益費・管理費
  if (tenant.commonAreaFee > 0) {
    const commonAreaFeeTax = isResidential
      ? 0
      : Math.floor(tenant.commonAreaFee * moneyLib.TAX_RATE)
    fees.push({
      label: "共益費・管理費",
      baseAmount: tenant.commonAreaFee,
      tax: commonAreaFeeTax,
      amount: tenant.commonAreaFee + commonAreaFeeTax,
    })
  }

  // 駐車場代（居住用でも課税対象）
  if (tenant.isParking && tenant.parkingFee > 0) {
    const parkingFeeTax = Math.floor(tenant.parkingFee * moneyLib.TAX_RATE)
    fees.push({
      label: "駐車場代",
      baseAmount: tenant.parkingFee,
      tax: parkingFeeTax,
      amount: tenant.parkingFee + parkingFeeTax,
    })
  }

  // その他費用
  if (tenant.otherFees && tenant.otherFees.length > 0) {
    tenant.otherFees.forEach((fee) => {
      fees.push({
        label: fee.label,
        baseAmount: fee.price,
        tax: fee.tax,
        amount: fee.price + fee.tax,
      })
    })
  }

  return fees
}

export function RoomDetailPage() {
  const { propertyId: propertyIdParam, roomId: roomIdParam } = useParams<{
    propertyId: string
    roomId: string
  }>()
  const navigate = useNavigate()

  if (!roomIdParam || Number.isNaN(Number(roomIdParam))) {
    return (
      <ErrorPage
        title="エラー"
        message="部屋IDが指定されていません。URLを確認してください。"
        backPath="/properties"
      />
    )
  }

  if (!propertyIdParam || Number.isNaN(Number(propertyIdParam))) {
    return (
      <ErrorPage
        title="エラー"
        message="物件IDが指定されていません。URLを確認してください。"
        backPath="/properties"
      />
    )
  }

  return (
    <ReactErrorBoundary>
      <Suspense fallback={<RoomDetailPageFallback />}>
        <RoomDetailPageContent
          roomId={roomIdParam}
          propertyId={propertyIdParam}
          navigate={navigate}
        />
      </Suspense>
    </ReactErrorBoundary>
  )
}

interface RoomDetailPageContentProps {
  roomId: string
  propertyId: string
  navigate: ReturnType<typeof useNavigate>
}

function RoomDetailPageContent({ roomId, propertyId, navigate }: RoomDetailPageContentProps) {
  const location = useLocation()
  const { canCreate, canUpdate } = usePermission()
  const [previewImage, setPreviewImage] = useState<PreviewImage | null>(null)

  // APIからデータをフェッチ
  const { data, refetch } = roomApi.useRoomGetSuspense(roomId, {
    withProperty: roomParams.RoomGetWithProperty.true,
    withPropertyOwner: roomParams.RoomGetWithPropertyOwner.true,
    withTenants: roomParams.RoomGetWithTenants.true,
    withTenantsOccupants: roomParams.RoomGetWithTenantsOccupants.true,
    withPlans: roomParams.RoomGetWithPlans.true,
    withOwnerMemos: roomParams.RoomGetWithOwnerMemos.true,
    withOwnerMemosFiles: roomParams.RoomGetWithOwnerMemosFiles.true,
  })

  // ページに戻ってきた時にデータを再取得
  useEffect(() => {
    const state = location.state as { refetch?: boolean } | null
    if (state?.refetch) {
      refetch()
    }
  }, [location.state, refetch])

  const room = data.room
  const property = room.property
  const tenant = roomLib.roomCurrentTenant(room)
  const pastTenants = roomLib.roomPastTenants(room)
  const planInfo = roomLib.getRoomServicePlanInfo(room)
  const ownerMemos = room.ownerMemos

  // 料金情報を生成
  const fees = tenant ? generateFeeDetails(tenant) : []
  const totalAmount = fees.reduce((sum, fee) => sum + fee.amount, 0)

  const handleEdit = () => {
    openInNewTab(`/properties/${propertyId}/rooms/${roomId}/edit`)
  }

  const handleTenantEdit = () => {
    if (tenant?.id) {
      openInNewTab(`/properties/${propertyId}/rooms/${roomId}/tenants/${tenant.id}/edit`)
    }
  }

  const handleTenantDetail = (tenantId: number) => {
    openInNewTab(`/tenants/${tenantId}`)
  }

  const handleTenantAdd = () => {
    openInNewTab(`/properties/${propertyId}/rooms/${roomId}/tenants/add`)
  }

  return (
    <div className="space-y-6">
      {/* Header - 戻るボタン */}
      <div className="flex items-center gap-2.5">
        <Button
          variant="ghost"
          size="icon"
          className="w-7 h-7 shadow-sm"
          onClick={() => navigate(`/properties/${propertyId}`)}
        >
          <ArrowLeft size={14} />
        </Button>
        <h2 className="text-sm font-semibold text-slate-600">物件詳細に戻る</h2>
      </div>

      {/* Title & Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">部屋詳細</h1>
        </div>
        <div className="flex gap-2.5">
          <Button
            variant="outline"
            onClick={() =>
              openInNewTab(`/tickets?propertyName=${encodeURIComponent(property?.name || "")}`)
            }
          >
            物件の案件を表示
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              openInNewTab(
                `/tickets?propertyName=${encodeURIComponent(property?.name || "")}&roomName=${encodeURIComponent(room.name)}`
              )
            }
          >
            部屋の案件を表示
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-3.5">
        <EchoesSyncCard
          idLabel="Echoes部屋ID"
          echoesId={room.echoesResidenceId}
          echoesUpdatedAt={room.echoesUpdatedAt}
          echoesFailedAt={room.echoesFailedAt}
        />
        <div className="flex gap-3.5">
          {/* 概要 Card */}
          <div className="flex-[7] bg-white rounded-lg shadow-sm">
            <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
              <div className="flex items-center gap-1.5">
                <Book size={14} />
                <span className="text-sm font-semibold text-slate-600">概要</span>
              </div>
              {canUpdate("rooms") && (
                <Button variant="outline" size="sm" onClick={handleEdit}>
                  <Edit size={14} className="mr-2" />
                  編集
                </Button>
              )}
            </div>
            <div className="p-5">
              <div className="grid grid-cols-7 gap-x-7.5 gap-y-4">
                {/* 1行目 */}
                <div>
                  <div className="text-xs font-medium text-slate-400 mb-2">部屋ID</div>
                  <div className="text-sm text-slate-600">{room.id}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-400 mb-2">物件名</div>
                  <div className="text-sm text-slate-600">{property?.name || "-"}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-400 mb-2">部屋番号</div>
                  <div className="text-sm text-slate-600">{room.name}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-400 mb-2">ステータス</div>
                  <div className="text-sm text-slate-600">{tenant ? "入居中" : "空室"}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-400 mb-2">オーナー名</div>
                  <div className="text-sm text-slate-600">{property?.owner?.name || "-"}</div>
                </div>
                <div className="col-span-2" />

                {/* 2行目 */}
                <div className="col-span-2">
                  <div className="text-xs font-medium text-slate-400 mb-2">住所</div>
                  <div className="text-sm text-slate-600">
                    {property
                      ? `${property.prefecture}${property.city}${property.town}${property.district}`
                      : "-"}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-400 mb-2">階数</div>
                  <div className="text-sm text-slate-600">{room.floor}階</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-400 mb-2">間取り</div>
                  <div className="text-sm text-slate-600">{room.layout}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-400 mb-2">面積</div>
                  <div className="text-sm text-slate-600">{room.size}㎡</div>
                </div>
                <div className="col-span-2" />
              </div>
            </div>
          </div>

          {/* プラン情報 Card */}
          <div className="flex-[3] bg-white rounded-lg shadow-sm">
            <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
              <div className="flex items-center gap-1.5">
                <Repeat size={14} />
                <span className="text-sm font-semibold text-slate-600">プラン情報</span>
              </div>
              <div className="h-9" />
            </div>
            <div className="p-5">
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">加入中のプラン</div>
                <div className="flex items-center gap-2">
                  <Label variant={planInfo.variant}>{planInfo.label}</Label>
                  {planInfo.suspended && <Label variant="planSuspended">サービス停止中</Label>}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-3.5">
          {/* 現在の賃借人 Card */}
          <div className="flex-1 bg-white rounded-lg shadow-sm">
            <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
              <div className="flex items-center gap-1.5">
                <User size={14} />
                <span className="text-sm font-semibold text-slate-600">現在の賃借人</span>
              </div>
              {tenant && canUpdate("tenants") && (
                <Button variant="outline" size="sm" onClick={handleTenantEdit}>
                  <Edit size={14} className="mr-2" />
                  編集
                </Button>
              )}
            </div>
            <div className="p-5">
              {tenant ? (
                <div className="grid grid-cols-2 gap-x-7.5 gap-y-4">
                  <div className="col-span-2">
                    <div className="text-xs font-medium text-slate-400 mb-2">用途</div>
                    <div className="text-sm text-slate-600">
                      {tenantLib.getPurposeLabel(tenant.purpose)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-400 mb-2">氏名</div>
                    <div className="text-sm text-slate-600">{tenant.name}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-400 mb-2">氏名(カナ)</div>
                    <div className="text-sm text-slate-600">{tenant.nameKana}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-400 mb-2">電話番号</div>
                    <div className="text-sm text-slate-600">
                      {formatPhone(tenant.phone1, tenant.phone2, tenant.phone3)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-400 mb-2">メールアドレス</div>
                    <div className="text-sm text-slate-600">{tenant.email}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-400 mb-2">
                      初回賃貸借契約開始日
                    </div>
                    <div className="text-sm text-slate-600">{tenant.startDate}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-400 mb-2">賃貸借契約終了日</div>
                    <div className="text-sm text-slate-600">{tenant.endDate ?? "-"}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-xs font-medium text-slate-400 mb-2">入居者</div>
                    {!tenant.occupants || tenant.occupants.length === 0 ? (
                      <div className="text-sm text-slate-400">入居者の登録はありません</div>
                    ) : (
                      <ul className="space-y-1.5">
                        {tenant.occupants.map((occupant) => {
                          const occupantWithContact = occupant as typeof occupant & {
                            phone?: string | null
                            note?: string | null
                          }
                          return (
                            <li
                              key={occupant.id}
                              className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm text-slate-600"
                            >
                              <span>{occupant.name}</span>
                              {occupant.nameKana && (
                                <span className="text-slate-400">{occupant.nameKana}</span>
                              )}
                              {occupantWithContact.phone && (
                                <span className="text-slate-400">
                                  電話番号: {occupantWithContact.phone}
                                </span>
                              )}
                              {occupant.birthday && (
                                <span className="text-slate-400">{occupant.birthday}</span>
                              )}
                              {occupantWithContact.note && (
                                <span className="basis-full text-slate-400">
                                  備考: {occupantWithContact.note}
                                </span>
                              )}
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                  {canCreate("tenants") && (
                    <div className="col-span-2 space-y-3 border-t border-slate-200 pt-5">
                      <div className="flex items-center gap-1.5">
                        <User size={14} />
                        <span className="text-sm font-semibold text-slate-600">次の賃借人</span>
                      </div>
                      <Button variant="outline" onClick={handleTenantAdd} className="w-full">
                        <Plus className="mr-2" size={16} />
                        次の賃借人を追加
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <EmptyCard text="現在の賃借人はいません" />
              )}
            </div>
            {!tenant && canCreate("tenants") && (
              <div className="px-5 pb-5">
                <Button variant="outline" onClick={handleTenantAdd} className="w-full">
                  <Plus className="mr-2" size={16} />
                  賃借人を追加
                </Button>
              </div>
            )}
          </div>

          {/* 料金情報 Card */}
          <div className="flex-1 bg-white rounded-lg shadow-sm">
            <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
              <div className="flex items-center gap-1.5">
                <CreditCard size={14} />
                <span className="text-sm font-semibold text-slate-600">料金情報</span>
              </div>
              <div className="h-9" />
            </div>
            <div className="p-5">
              {fees.length > 0 ? (
                <div className="space-y-2.5">
                  {fees.map((fee, index) => (
                    <div key={`${fee.label}-${fee.amount}`}>
                      <div className="flex justify-between items-start">
                        <div className="text-sm font-medium text-slate-400">{fee.label}</div>
                        <div className="flex flex-col items-end">
                          <div className="text-base font-medium text-slate-600">
                            ¥{fee.amount.toLocaleString("ja-JP")}
                          </div>
                          {fee.tax > 0 && (
                            <div className="text-sm text-slate-400">
                              ¥{fee.baseAmount.toLocaleString("ja-JP")} + 税¥
                              {fee.tax.toLocaleString("ja-JP")}
                            </div>
                          )}
                        </div>
                      </div>
                      {index < fees.length - 1 && <hr className="my-2.5 border-slate-200" />}
                    </div>
                  ))}
                  <hr className="my-2.5 border-slate-200" />
                  <div className="flex justify-between items-start">
                    <div className="text-sm font-medium text-slate-400">月額合計</div>
                    <div className="flex flex-col items-end">
                      <div className="text-base font-medium text-slate-600">
                        ¥{totalAmount.toLocaleString("ja-JP")}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <EmptyCard text="料金情報はありません" />
              )}
            </div>
          </div>
        </div>
        {/* オーナーメモ Card */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
            <div className="flex items-center gap-1.5">
              <FileText size={14} />
              <span className="text-sm font-semibold text-slate-600">オーナーメモ</span>
            </div>
            <div className="h-9" />
          </div>
          <div className="p-5">
            {ownerMemos ? (
              <div className="grid grid-cols-1 gap-4">
                {ownerMemos.map((ownerMemo) => (
                  <div
                    key={ownerMemo.id}
                    className="border border-slate-200 rounded-md p-3.5 h-full flex flex-col"
                  >
                    <div className="flex gap-3.5 flex-1">
                      <div className="flex-1 space-y-1.5">
                        <div className="text-xs font-medium text-slate-400">
                          {dateLib.formatDateTime(ownerMemo.createdAt)}
                        </div>
                        <div className="text-sm text-slate-600">{ownerMemo.title}</div>
                        <div className="text-sm text-slate-500">{ownerMemo.body}</div>
                      </div>
                      <div className="flex gap-4">
                        {ownerMemo.files?.map((file) => (
                          <button
                            type="button"
                            key={file.id}
                            onClick={() =>
                              setPreviewImage({
                                url: file.url,
                                title: ownerMemo.title,
                                fileName: file.fileName,
                              })
                            }
                            className="w-[188px] h-[141px] rounded-md bg-slate-200 bg-cover bg-center flex-shrink-0 cursor-zoom-in"
                            style={{ backgroundImage: `url(${file.url})` }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyCard text="オーナーメモはありません" />
            )}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm">
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
            <div className="flex items-center gap-1.5">
              <Users size={14} />
              <span className="text-sm font-semibold text-slate-600">過去の賃借人</span>
            </div>
            <div className="h-9" />
          </div>
          <div className="p-5">
            {pastTenants.length === 0 ? (
              <EmptyCard text="過去の賃借人はいません" />
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">賃借人氏名</TableHead>
                      <TableHead className="whitespace-nowrap">賃借人氏名(カナ)</TableHead>
                      <TableHead className="whitespace-nowrap">入居期間</TableHead>
                      <TableHead className="whitespace-nowrap">初回契約開始日</TableHead>
                      <TableHead className="whitespace-nowrap">契約終了日</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pastTenants.map((pastTenant) => (
                      <TableRow key={pastTenant.id}>
                        <TableCell>{pastTenant.name}</TableCell>
                        <TableCell>{pastTenant.nameKana}</TableCell>
                        <TableCell>
                          {TenantUtils.formatTenantDuration(
                            pastTenant.startDate,
                            pastTenant.endDate
                          )}
                        </TableCell>
                        <TableCell>{pastTenant.startDate}</TableCell>
                        <TableCell>{pastTenant.endDate || "-"}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal size={16} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[200px]">
                              <DropdownMenuItem
                                onClick={() => {
                                  handleTenantDetail(pastTenant.id)
                                }}
                              >
                                詳細
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </div>
      <ImagePreviewDialog image={previewImage} onClose={() => setPreviewImage(null)} />
    </div>
  )
}

function RoomDetailPageFallback() {
  return (
    <div className="p-10 flex flex-col items-center justify-center">
      <Spinner className="text-slate-600 size-10 transition-opacity" />
    </div>
  )
}
